// Pick a replay start index that guarantees both a warmup window of visible
// history on the left and a minimum hidden future to actually replay.

export interface StartOpts {
  /** bars that must already be visible at the start (so the chart looks full) */
  minWarmup: number
  /** bars that MUST remain hidden after the start (something to discover) */
  minFuture: number
}

export class NotEnoughDataError extends Error {
  constructor(total: number, opts: StartOpts) {
    super(
      `dataset too small: need >= ${opts.minWarmup + opts.minFuture + 1} bars for warmup ${opts.minWarmup} + future ${opts.minFuture}, got ${total}`,
    )
    this.name = 'NotEnoughDataError'
  }
}

/** Valid start indices live in [minWarmup, total - minFuture). */
export function startBand(total: number, opts: StartOpts): { lo: number; hi: number } {
  const lo = opts.minWarmup
  const hi = total - opts.minFuture // exclusive
  return { lo, hi }
}

export function pickStart(
  total: number,
  opts: StartOpts,
  rnd: () => number = Math.random,
): number {
  const { lo, hi } = startBand(total, opts)
  if (hi <= lo) throw new NotEnoughDataError(total, opts)
  // uniform integer in [lo, hi)
  return lo + Math.floor(rnd() * (hi - lo))
}

/** Pick warmup/future that fit the dataset (small sets get smaller windows). */
export function adaptiveStartOpts(total: number): StartOpts {
  const minWarmup = Math.min(120, Math.max(10, Math.floor(total * 0.2)))
  const minFuture = Math.min(200, Math.max(10, Math.floor(total * 0.5)))
  return { minWarmup, minFuture }
}

/** Clamp a desired start index into the valid band. */
export function clampStart(desired: number, total: number, opts: StartOpts): number {
  const { lo, hi } = startBand(total, opts)
  if (hi <= lo) throw new NotEnoughDataError(total, opts)
  return Math.min(hi - 1, Math.max(lo, desired))
}
